import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TrendingUp, TrendingDown, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { ttStatsAPI } from '../services/api';
import { EloPreview } from '../types/api.types';
import { formatWithSign } from '../utils/dateUtils';
import UnifiedFooter from '../components/UnifiedFooter';
import AppPromotion from '../components/AppPromotion';
import { useOptimizedLogo } from '../hooks/useOptimizedLogo';
import AnimatedText from '../components/AnimatedText';

const CalculatorPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialElo = searchParams.get('elo');
  
  const [player1Elo, setPlayer1Elo] = useState<string>(initialElo || '');
  const [player2Elo, setPlayer2Elo] = useState<string>('');
  const [preview, setPreview] = useState<EloPreview | null>(null);
  const [hasCalculated, setHasCalculated] = useState(false);
  const logoSrc = useOptimizedLogo(true); // Prefer static for calculator page

  const calculatePreview = useCallback(async () => {
    if (!player1Elo || !player2Elo || isNaN(Number(player1Elo)) || isNaN(Number(player2Elo))) {
      setPreview(null);
      setHasCalculated(false);
      return;
    }

    try {
      const data = await ttStatsAPI.getEloPreview(Number(player1Elo), Number(player2Elo));
      setPreview(data);
      setHasCalculated(true);
    } catch (error) {
      console.error('Error calculating preview:', error);
      setHasCalculated(false);
    }
  }, [player1Elo, player2Elo]);

  useEffect(() => {
    if (player1Elo && player2Elo && !isNaN(Number(player1Elo)) && !isNaN(Number(player2Elo))) {
      calculatePreview();
    }
  }, [player1Elo, player2Elo, calculatePreview]);

  const quickSelections = [
    { label: '1000', value: '1000' },
    { label: '1200', value: '1200' },
    { label: '1400', value: '1400' },
    { label: '1600', value: '1600' },
    { label: '1800', value: '1800' },
    { label: '2000', value: '2000' },
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 flex flex-col transition-colors duration-300">
      {/* Floating App Promotion - only show after calculation */}
      {hasCalculated && <AppPromotion compact />}
      
      <div className="flex-1 flex flex-col items-center justify-center px-4">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, type: "spring", stiffness: 100 }}>
          <motion.img 
            src={logoSrc} 
            alt="TTStats" 
            className="h-24 w-auto mb-8 rounded-xl"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 400 }}
          />
        </motion.div>
        
        {/* Title */}
        <div>
          <AnimatedText
            text="ELO Calculator"
            className="text-3xl font-light text-gray-800 dark:text-gray-100 mb-2 text-center transition-colors duration-300"
            delay={0.6}
            staggerDelay={0.025}
          />
          <AnimatedText
            text="Calculate match probabilities and rating changes"
            className="text-sm text-gray-500 dark:text-gray-400 mb-8 text-center transition-colors duration-300"
            delay={0.9}
            staggerDelay={0.01}
          />
        </div>

        {/* Calculator inputs */}
        <motion.div 
          className="w-full max-w-md space-y-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}>
          {/* Player 1 */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 400 }}>
            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2 transition-colors duration-300">Your ELO</label>
            <input
              type="number"
              value={player1Elo}
              onChange={(e) => setPlayer1Elo(e.target.value)}
              placeholder="Enter your ELO rating"
              className="w-full px-4 py-3 text-lg border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-[#1C7AD6] focus:shadow-lg transition-all duration-300 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
            {!initialElo && (
              <div className="flex gap-2 mt-2">
                {quickSelections.slice(0, 3).map(({ label, value }) => (
                  <motion.button
                    key={value}
                    onClick={() => setPlayer1Elo(value)}
                    className="px-3 py-1 text-xs text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-full hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-300"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {label}
                  </motion.button>
                ))}
              </div>
            )}
          </motion.div>

          {/* VS divider */}
          <div className="text-center text-gray-400 dark:text-gray-500 text-sm transition-colors duration-300">vs</div>

          {/* Player 2 */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 400 }}>
            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2 transition-colors duration-300">Opponent ELO</label>
            <input
              type="number"
              value={player2Elo}
              onChange={(e) => setPlayer2Elo(e.target.value)}
              placeholder="Enter opponent's ELO rating"
              className="w-full px-4 py-3 text-lg border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-[#1C7AD6] focus:shadow-lg transition-all duration-300 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
            <div className="flex gap-2 mt-2">
              {quickSelections.map(({ label, value }) => (
                <motion.button
                  key={value}
                  onClick={() => setPlayer2Elo(value)}
                  className="px-3 py-1 text-xs text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-full hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-300"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {label}
                </motion.button>
              ))}
            </div>
          </motion.div>
        </motion.div>

        {/* Results */}
        {preview && (
          <motion.div
            className="w-full max-w-md mt-8 p-6 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 transition-colors duration-300"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{
              type: "spring",
              stiffness: 100,
              damping: 15
            }}>
            {/* Win probability */}
            <motion.div 
              className="text-center mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}>
              <motion.div
                className="text-5xl font-light text-gray-900 dark:text-gray-100 mb-1 transition-colors duration-300"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 200,
                  delay: 0.3
                }}>
                {Math.round(preview.win_probability)}%
              </motion.div>
              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium transition-colors duration-300">Win Probability</p>
            </motion.div>

            {/* Rating changes */}
            <div className="grid grid-cols-2 gap-4">
              <motion.div
                className="text-center p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                whileHover={{ scale: 1.05 }}>
                <div className="flex items-center justify-center mb-2">
                  <TrendingUp className="h-4 w-4 text-green-600 mr-1" />
                  <span className="text-lg font-medium text-green-600">
                    {formatWithSign(preview.elo_change_win)}
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 font-medium transition-colors duration-300">If Win</p>
              </motion.div>
              <motion.div
                className="text-center p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                whileHover={{ scale: 1.05 }}>
                <div className="flex items-center justify-center mb-2">
                  <TrendingDown className="h-4 w-4 text-red-600 dark:text-red-400 mr-1" />
                  <span className="text-lg font-medium text-red-600 dark:text-red-400">
                    {formatWithSign(preview.elo_change_loss)}
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 font-medium transition-colors duration-300">If Loss</p>
              </motion.div>
            </div>

            {/* Match insight */}
            {Math.abs(Number(player1Elo) - Number(player2Elo)) > 200 && (
              <motion.div
                className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl flex items-start gap-2 border border-blue-100 dark:border-blue-800/40 transition-colors duration-300"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}>
                <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-blue-800 dark:text-blue-300 font-medium transition-colors duration-300">
                    {Number(player1Elo) > Number(player2Elo)
                      ? `You are heavily favored (${Math.abs(Number(player1Elo) - Number(player2Elo))} points higher)`
                      : `Opponent is heavily favored (${Math.abs(Number(player1Elo) - Number(player2Elo))} points higher)`
                    }
                  </p>
                </div>
              </motion.div>
            )}

          </motion.div>
        )}
      </div>

      {/* Unified Footer */}
      <UnifiedFooter variant="promotion" />
    </div>
  );
};

export default CalculatorPage;